import {
  IconDownload,
  IconEye,
  IconFile,
  IconLoader2,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  fetchCustomerNoteAttachmentBlob,
  type CustomerNoteAttachment,
} from "../../api/queries/customerOrganizationQueries";
import { formatFileSize } from "../../utils/utilsFunctions/functions";
import { CustomerNoteAttachmentPreviewModal } from "./CustomerNoteAttachmentPreviewModal";
import { isPreviewableCustomerNoteAttachment } from "./customerNoteAttachments";

type CustomerNoteAttachmentListProps = {
  customerOrgId: number;
  noteId: number;
  attachments: CustomerNoteAttachment[];
};

export function CustomerNoteAttachmentList({
  customerOrgId,
  noteId,
  attachments,
}: CustomerNoteAttachmentListProps) {
  const [previewAttachment, setPreviewAttachment] =
    useState<CustomerNoteAttachment | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-3 border-t border-slate-200 pt-3">
        <p className="mb-2 text-xs font-medium text-slate-600">
          Attachments ({attachments.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.id}
              customerOrgId={customerOrgId}
              noteId={noteId}
              attachment={attachment}
              isDownloading={downloadingId === attachment.id}
              onPreview={() => setPreviewAttachment(attachment)}
              onDownloadingChange={(downloading) =>
                setDownloadingId(downloading ? attachment.id : null)
              }
            />
          ))}
        </div>
      </div>

      {previewAttachment ? (
        <CustomerNoteAttachmentPreviewModal
          isOpen
          customerOrgId={customerOrgId}
          noteId={noteId}
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      ) : null}
    </>
  );
}

function AttachmentChip({
  customerOrgId,
  noteId,
  attachment,
  isDownloading,
  onPreview,
  onDownloadingChange,
}: {
  customerOrgId: number;
  noteId: number;
  attachment: CustomerNoteAttachment;
  isDownloading: boolean;
  onPreview: () => void;
  onDownloadingChange: (downloading: boolean) => void;
}) {
  const canPreview = isPreviewableCustomerNoteAttachment(attachment);

  const handleDownload = async () => {
    onDownloadingChange(true);
    try {
      const blob = await fetchCustomerNoteAttachmentBlob(
        customerOrgId,
        noteId,
        attachment.id,
        "attachment",
        undefined,
        attachment.filename,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // Download failures are silent here; preview modal surfaces errors.
    } finally {
      onDownloadingChange(false);
    }
  };

  return (
    <div className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
      <IconFile size={14} className="shrink-0 text-slate-500" />
      <span
        className="max-w-[12rem] truncate font-medium"
        title={attachment.filename}
      >
        {attachment.filename}
      </span>
      <span className="shrink-0 text-slate-400">
        {formatFileSize(attachment.file_size_bytes)}
      </span>
      <div className="ml-0.5 flex shrink-0 items-center gap-0.5">
        {canPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800"
            aria-label={`Preview ${attachment.filename}`}
            title="Preview"
          >
            <IconEye size={14} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className="rounded p-1 text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-50"
          aria-label={`Download ${attachment.filename}`}
          title="Download"
        >
          {isDownloading ? (
            <IconLoader2 size={14} className="animate-spin" />
          ) : (
            <IconDownload size={14} />
          )}
        </button>
      </div>
    </div>
  );
}
